import * as THREE from 'three';
import vertexFbo from './shaders/fbo/vertex.glsl';
import fragmentFbo from './shaders/fbo/fragment.glsl';
import vertexParticles from './shaders/particles/vertex.glsl';
import fragmentParticles from './shaders/particles/fragment.glsl';

// Canvas setup
const canvas = document.querySelector('canvas.webgl');

// Scene setup
const scene = new THREE.Scene();

// Size setup
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
};

// Renderer setup
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(sizes.pixelRatio);
renderer.setClearColor('#000000');

// Camera setup
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
camera.position.set(0, 0, 3);
scene.add(camera);

// Render Target Helper
const getRenderTarget = (size) => {
    return new THREE.WebGLRenderTarget(size, size, {
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        colorSpace: THREE.SRGBColorSpace,
        format: THREE.RGBAFormat,
        type: THREE.FloatType,
    });
};

const size = Math.floor(2048 * 0.5);
const totalPoints = size * size;

// FBO Setup
let fbo = getRenderTarget(size);
let fbo1 = getRenderTarget(size);

const data = new Float32Array(totalPoints * 4);
const infoArray = new Float32Array(totalPoints * 4);

const aspectRatio = sizes.width / sizes.height;
const scale = 2 * 3 * Math.tan(0.610865 / 2);

for (let i = 0; i < totalPoints; i++) {
    let i4 = i * 4;
    let x = (Math.random() - 0.5) * scale * aspectRatio;
    let y = (Math.random() - 0.5) * scale;

    data[i4] = x;
    data[i4 + 1] = y;
    data[i4 + 2] = 0;
    data[i4 + 3] = 1;

    infoArray[i4] = x;
    infoArray[i4 + 1] = y;
    infoArray[i4 + 2] = 1;
    infoArray[i4 + 3] = 1;
}

const createDataTexture = (array) => {
    const texture = new THREE.DataTexture(array, size, size, THREE.RGBAFormat, THREE.FloatType);
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.needsUpdate = true;
    return texture;
};

const dataTexture = createDataTexture(data);
const infoTexture = createDataTexture(infoArray);

// FBO Scene
const fboScene = new THREE.Scene();
const fboCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);

const fboMaterial = new THREE.ShaderMaterial({
    vertexShader: vertexFbo,
    fragmentShader: fragmentFbo,
    uniforms: {
        u_time: { value: 0 },
        u_positions: { value: dataTexture },
        u_info: { value: infoTexture },
        u_mousePos: { value: new THREE.Vector2(999, 999) },
        u_res: { value: new THREE.Vector2(size, size) }
    }
});

// Mouse move interaction
const sphere = new THREE.Mesh(new THREE.SphereGeometry(0.01, 32, 32), new THREE.MeshBasicMaterial({ color: 'red' }));
scene.add(sphere);

window.addEventListener('mousemove', (event) => {
    const ndcX = (event.clientX / sizes.width) * 2 - 1;
    const ndcY = -(event.clientY / sizes.height) * 2 + 1; // Invert Y-axis
    const projected = new THREE.Vector3(ndcX, ndcY, 0);
    projected.unproject(camera);
    const direction = projected.sub(camera.position).normalize();
    const distance = -camera.position.z / direction.z;
    const worldPosition = camera.position.clone().add(direction.multiplyScalar(distance));
    sphere.position.set(worldPosition.x, worldPosition.y, 0);
    fboMaterial.uniforms.u_mousePos.value.set(worldPosition.x, worldPosition.y);
});

const fboMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), fboMaterial);
fboScene.add(fboMesh);

// Initial Render to FBO
renderer.setRenderTarget(fbo);
renderer.render(fboScene, fboCamera);
renderer.setRenderTarget(fbo1);
renderer.render(fboScene, fboCamera);
renderer.setRenderTarget(null);

// Particle setup
const geometry = new THREE.BufferGeometry();
const vertices = new Float32Array(totalPoints * 3);
const uvs = new Float32Array(totalPoints * 2);

for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
        let index = i + j * size;
        let i3 = index * 3;
        let i2 = index * 2;
        vertices.set([i / size, j / size, 0], i3);
        uvs.set([i / size, j / size], i2);
    }
}

geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));

const particleMaterial = new THREE.ShaderMaterial({
    vertexShader: vertexParticles,
    fragmentShader: fragmentParticles,
    transparent: true,
    depthTest: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
        u_positions: { value: fbo.texture },
        u_res: { value: new THREE.Vector2(sizes.width, sizes.height) },
        u_time: { value: 0 },
        u_alpha: { value: 1 }
    }
});

const particles = new THREE.Points(geometry, particleMaterial);
scene.add(particles);

// Resize handling
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2);

    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(sizes.pixelRatio);
});

// Animation loop
const clock = new THREE.Clock();
const tick = () => {
    const elapsedTime = clock.getElapsedTime();

    // Update shader time uniform
    fboMaterial.uniforms.u_time.value = elapsedTime;
    particleMaterial.uniforms.u_time.value = elapsedTime;

    // Swap FBOs
    [fbo, fbo1] = [fbo1, fbo];

    // Render FBO
    fboMaterial.uniforms.u_positions.value = fbo1.texture;
    particleMaterial.uniforms.u_positions.value = fbo.texture;

    renderer.setRenderTarget(fbo);
    renderer.render(fboScene, fboCamera);
    renderer.setRenderTarget(null);

    // Final scene render
    renderer.render(scene, camera);
    requestAnimationFrame(tick);
};

tick();
